/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */
import {
  Track, TrackType
}                          from './track';
import {
  DecisionPoint,
  JunctionLayout,
  SwitchingLayout
}                          from './decision.point';
import { Logger }          from '../../util/logger';

export enum InterLinkType {
  SINGLE   = 0,
  DOUBLE   = 1,
  PARKING  = 2,
  TERMINAL = 3,
  JUNCTION = 4
}

export class InterLink {

  /** @var _tracks the bundle of links/tracks in this interlink */
  public _tracks: Track[]       = [];

  /** @var _before the previous (more westward decision point) */
  public _before: DecisionPoint|null = null;

  /** @var _after the next (more eastward decision point) */
  public  _after: DecisionPoint|null = null;

  /** @var _order the milepost at the start of the interlink, (eastward) ordering */
  public  _order: number        = -1;

  /** @var _index the position in the interlinks array (used for relative jumps to other interlinks. */
  public  _index: number        = -1;

  /** @var _sIndex the position within just the subset for its subdivision */
  public _sIndex: number        = -1;

  /** @var _type kind -- S is single track, D is double/multi-track and P is parking (sidings) */
  public   _type: InterLinkType = InterLinkType.JUNCTION;

  /** @var _name the name of this interlink (via its decision points) */
  public   _name: string        = '';

  /** true if this interlink is the very last one */
  public _westMost: boolean = false;

  /** true if this interlink is the very first one */
  public _eastMost: boolean = false;

  /** @brief helper to convert interlink types to strings */
  public static typeToString(code: InterLinkType) {

    switch (code) {
      case InterLinkType.SINGLE:   return 'S';
      case InterLinkType.DOUBLE:   return 'D';
      case InterLinkType.PARKING:  return 'P';
      case InterLinkType.TERMINAL: return 'T';
      case InterLinkType.JUNCTION: return 'J';
    }

    return '';
  }

  /** @brief standard constructor */
  constructor(order: number, tracks: Track[], before: DecisionPoint|null, after: DecisionPoint|null) {

    const il: InterLink = this;

    il._order  = order;
    il._tracks = tracks;
    il._before = before;
    il._after  = after;

    il._tracks.sort((a: Track, b: Track): number => {

      /* mains before sidings */

      if ((a._type === TrackType.JUNCTION) && (b._type !== TrackType.JUNCTION)) {

        return 1;
      }

      if ((a._type !== TrackType.JUNCTION) && (b._type === TrackType.JUNCTION)) {

        return -1;
      }

      if ((a._type === TrackType.MAIN) && (b._type === TrackType.PARKING)) {

        return -1;
      }

      if ((a._type === TrackType.PARKING) && (b._type === TrackType.MAIN)) {

        return 1;
      }

      if (a._track < b._track) {

        return -1;
      }

      if (a._track > b._track) {

        return 1;
      }

      return 0;
    });

    /* let the decision points know which side this interlink is on... */

    [il._before, il._after].forEach((dp: DecisionPoint|null): void => {

      if (dp === null) {

        /* nothing to do */

        return ;
      }

      /*
       * if the interlink mileage and the decision point mileage are the same, we can still tell
       * which side to use by looking at the neighbouring decision points, the internal link will be
       * between one of the neighbours and this decision point...so that's the side it should
       * be on.
       *
       */

      const  dps: DecisionPoint[] = dp.neighbours();
      const oMin: number          = il.minMiles();
      const oMax: number          = il.maxMiles();

      if (oMin === oMax) {

        /*
         * the terminals on either end only have one neighbour, we don't have to process them
         * they'll get processed by being neighbours to other nodes.
         *
         */

        if (oMin === 0) {

          /* far east side */

          dp._eastSide = il;

        } else {

          /* must be far west side */

          dp._westSide = il;
        }

        return ;
      }

      dps.forEach((x: DecisionPoint) => {

        const min: number = Math.min(x._mileage, dp._mileage);
        const max: number = Math.max(x._mileage, dp._mileage);

        if ((oMin < min) || (oMax > max)) {

          /* not between */

          return;
        }

        if (x._mileage > dp._mileage) {

          dp._westSide = il;

          x._eastSide = il;

        } else {

          dp._eastSide = il;

          x._westSide = il;
        }

      });

    });

    /* figure out our name */

    if (il._before != null) {

      il._name = il._before._name.trim();
    }

    if (il._name !== '') {

      if (il._after != null) {

        il._name += ' / ';
      }
    }

    if (il._after != null) {

      il._name += il._after._name.trim();
    }

    /* figure out the general type of this bundle of tracks, the summary type ... */

    let notJunction: number = 0;

    for (const x of il._tracks) {

      if (x._type === TrackType.JUNCTION) {

        continue;
      }

      if (x._type === TrackType.TERMINAL) {

        il._type = InterLinkType.TERMINAL;

        break;
      }

      /* normally sets of links either all M tracks or all P tracks, it never gets mixed. */

      if (x._type === TrackType.MAIN) {

        il._type = InterLinkType.SINGLE;

        notJunction++;

        continue;
      }

      /* note, we don't expect to ever see just a P0, if its P there should be at least two tracks */

      if (x._type === TrackType.PARKING) {

        il._type = InterLinkType.PARKING;

        notJunction++;

        break;
      }
    }

    /* if we found more than one M track then it's a double track */

    if (il._type === InterLinkType.SINGLE) {

      if (notJunction >= 2) {

        il._type = InterLinkType.DOUBLE;
      }
    }

    switch (il._type) {

      case InterLinkType.JUNCTION: {
        il._name = '::J::' + il._name;
        break;
      }

      case InterLinkType.TERMINAL: {
        il._name = '::T::' + il._name;
        break;
      }

      case InterLinkType.PARKING: {
        il._name = '::P::' + il._name;
        break;
      }

      case InterLinkType.SINGLE: {
        il._name = '::S::' + il._name;
        break;
      }

      case InterLinkType.DOUBLE: {
        il._name = '::D::' + il._name;
        break;
      }
    }

    /*
     * track normalization: some models have track number start at 1 instead of 0, and/or may
     * have independent number sequences by type, i.e. same bundle could have M1 and P1
     * at the same time.  We have to maie sure that for double track we have M0 and M1,
     * and for siding we start at P0 and go up to PN-1.  Junction tracks can be numbered as is,
     * but should start at 0.  Terminals should have only main tracks, and they should start at 0.
     *
     *     PERMISSIBLE TYPES
	   *
     *     M0         - Single track
	   *     M0/M1      - Double track
   	 *     T0...Tn    - Terminal
   	 *     P0/P1...Pn - Parking (siding)
   	 *
     */
    /* SN-1379/1411/1412 - With Multi-track enabled, we don't want to do any of this track renumbering.  the CN users must correctly write the track numbers and types*/
    if (false) {

      if (il._type === InterLinkType.SINGLE) {

        let found: boolean = false;

        for (const t of il._tracks) {

          if (t._type === TrackType.MAIN) {

            if (found) {

              Logger.error('[interlink]', 'corrupt, SINGLE has more than one main: ', this);

            } else {

              found = true;
              t._track = 0;
            }
          }
        }

      } else if (il._type === InterLinkType.TERMINAL) {

        let m: number = 9999;

        for (const t of il._tracks) {

          if ((t._track < m) && (t._type === TrackType.TERMINAL)) {

            m = t._track;
          }

          if (t._type !== TrackType.TERMINAL) {

            Logger.error('[interlink]', 'corrupt, TERMINAL has non-T tracks: ', this);
          }
        }

        if (m === 9999) {

          Logger.error('[interlink]', 'corrupt, TERMINAL has no T tracks: ', this);

        } else {

          if (m !== 0) {

            /* all tracks have to be normalized to 0 based */

            let i: number = 0;

            for (const t of il._tracks) {

              if (t._type !== TrackType.TERMINAL) {
                continue;
              }

              t._track = i;

              i++;
            }
          }
        }

      } else if (il._type === InterLinkType.DOUBLE) {

        if (tracks.length !== 2) {

          Logger.error('[interlink]', 'corrupt, DOUBLE has more than 2 track: ', this);

        } else {

          il._tracks[0]._track = 0;
          il._tracks[1]._track = 1;
        }

        /* the lowest track is the south track */

        il._tracks[0]._southTrack = true;

      } else if (il._type === InterLinkType.PARKING) {

        /*
         * tracks are in order, but can be a mix of main, parking and junction, all main/parking should be parking,
         * and track # be zero based.
         *
         */

        let i: number = 0;

        for (const t of il._tracks) {

          if (t._type === TrackType.MAIN) {

            t._track = i;
            t._type = TrackType.PARKING;

            i++;

          } else {

            if (t._type === TrackType.PARKING) {

              t._track = i;

              i++;

            }
          }
        }
      }
    }
    Logger.debug('[interlink]', il._name + ': ', il);
  }

  /* is this the minimum mileage interlink */
  public isEastMost(): boolean {
    return this._eastMost;
  }

  /* is this the maximum mileage interlink */
  public isWestMost(): boolean {
    return this._westMost;
  }

  /* fetch the maximum mileage this interlink has via its decision points */
  public maxMiles(): number {

    let a: number = 0;
    let b: number = 0;

    if (this._after !== null) {

      a  = this._after._mileage;
    }

    if (this._before !== null) {

      b  = this._before._mileage;
    }

    return Math.max(a,b);
  }

  /* fetch the maximum mileage this interlink has via its decision points */
  public minMiles(): number {

    let a: number = Number.MAX_SAFE_INTEGER;
    let b: number = Number.MAX_SAFE_INTEGER;

    if (this._after !== null) {

      a  = this._after._mileage;
    }

    if (this._before !== null) {

      b  = this._before._mileage;
    }

    return Math.min(a,b);
  }

  /** @brief return the decision points ordered for eastward mileage */
  public dps(): [DecisionPoint|null, DecisionPoint|null] {

    const dp1: DecisionPoint|null = this._after;
    const dp2: DecisionPoint|null = this._before;

    if (dp1 == null) {

      return [dp2, null];
    }

    if (dp2 == null) {

      return [dp1, null];
    }

    if (dp1._mileage > dp2._mileage) {

      return [dp1, dp2];
    }

    return [dp2, dp1];
  }

  /** @brief check to see if this interlink contains the given distance */
  contains(distance: number) {

    const [dp1, dp2]      = this.dps();

    if (dp2 == null) {

      if ((distance > 0) && (dp1 && distance <= dp1._mileage)) {

        /* most eastward link */

        return this;
      }

      if (dp1 && dp1._order === 0) {

        if (distance >= dp1._mileage) {

          /* most westward link */

          return this;
        }
      }

    } else {

      if ((dp1 && distance <= dp1._mileage) && (dp2 && distance >= dp2._mileage)) {

        /* internal interlink */

        return this;
      }
    }

    /* nope */

    return null;
  }

  /** @brief helper to generate the switch layout summary string the given track */
  public switchLayout(t: Track): string {

    /* the junction layout */

    let sStr: string        = '';
    let   dp: DecisionPoint|null = this._before;

    if (t._type === TrackType.JUNCTION) {

      /* for junctions, make sure we are using the end that is connected to the network */

      dp = t._after;

      if (dp == null) {

        dp = t._before;

      } else {

        if ((this._before && dp._id !== this._before._id) && (this._after && dp._id !== this._after._id)) {

          /* it's not actually part of this bundle */

          return '';
        }
      }
    }

    if (t._peers && t._peers._sIndex !== this._sIndex) {

      /* the given track is not part of this bundle */

      return '';
    }

    switch (dp && dp.switchLayout(t)) {

      case SwitchingLayout.AllCross: {

        sStr = 'X' + '\u2798' + '\u279a';

        break;
      }

      case SwitchingLayout.NoCross: {

        if (this._type === InterLinkType.DOUBLE) {

          sStr = 'X' + '\u21c6';

        } else {

          sStr = 'X' + '\u2212';

        }

        break;
      }

      case SwitchingLayout.StraightThrough: {

        if (this._type === InterLinkType.DOUBLE) {

          sStr = 'X' + '\u21c6';

        } else {

          sStr = 'X' + '\u2212';

        }

        break;
      }

      case SwitchingLayout.LeftHandCross: {

        sStr = 'X' + '\u2798';

        break;
      }

      case SwitchingLayout.RightHandCross: {

        sStr = 'X' + '\u279a';

        break;
      }
    }

    return sStr;
  }

  /** @brief helper to fetch the junction configuration summary string fora track */
  public junctionLayout(): string {

    /* the junction layout */

    let jStr: string = '';

    if (!this._before) {

      return '';
    }
    switch (this._before.junctionLayout()) {

      case JunctionLayout.AllJunction: {
        jStr = 'J' + '\u2193' + '\u2191';
        break;
      }

      case JunctionLayout.EastJunction: {
        jStr = 'J' + '\u2193';
        break;
      }

      case JunctionLayout.WestJunction: {
        jStr = 'J' + '\u2191';
        break;
      }

      case JunctionLayout.NoJunction: {
        jStr = 'J' + '\u2212';
        break;
      }
    }

    return jStr;
  }

}
